import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { PROFILE } from "../../config/profile";

const IDLE_MS = 90_000;
const STAR_COUNT = 260;
const WAKE_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "wheel",
  "touchstart",
];

// 98-style "Starfield Simulation" plus a DVD-logo style bouncing name.
// Kicks in after 90s idle; any input dismisses it.
const Screensaver = () => {
  const [active, setActive] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const arm = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        // Don't interrupt someone reading an embedded project site: events
        // inside an iframe never reach this document.
        if (document.activeElement?.tagName === "IFRAME") {
          arm();
          return;
        }
        setActive(true);
      }, IDLE_MS);
    };
    const wake = () => {
      setActive(false);
      arm();
    };
    arm();
    WAKE_EVENTS.forEach((event) =>
      window.addEventListener(event, wake, { passive: true })
    );
    return () => {
      clearTimeout(timer);
      WAKE_EVENTS.forEach((event) => window.removeEventListener(event, wake));
    };
  }, []);

  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const spawn = () => ({
      x: (Math.random() - 0.5) * canvas.width,
      y: (Math.random() - 0.5) * canvas.height,
      z: Math.random() * canvas.width,
    });
    const stars = Array.from({ length: STAR_COUNT }, spawn);

    const label = PROFILE.osName;
    const colors = [
      "#ff5f5f",
      "#5fff7a",
      "#5fb4ff",
      "#ffd84a",
      "#ff7bff",
      "#5ffff0",
    ];
    let colorIndex = 0;
    const logo = { x: 80, y: 80, dx: 2.2, dy: 1.8 };

    let frame = 0;
    const draw = () => {
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;

      stars.forEach((star, i) => {
        star.z -= 6;
        if (star.z <= 1) stars[i] = { ...spawn(), z: canvas.width };
        const k = 160 / star.z;
        const x = cx + star.x * k * 2;
        const y = cy + star.y * k * 2;
        if (x < 0 || x > canvas.width || y < 0 || y > canvas.height) {
          stars[i] = { ...spawn(), z: canvas.width };
          return;
        }
        const size = Math.max(1, 3 - star.z / (canvas.width / 3));
        const shade = Math.min(255, Math.round(255 - star.z / 4));
        ctx.fillStyle = `rgb(${shade},${shade},${shade})`;
        ctx.fillRect(x, y, size, size);
      });

      ctx.font = "bold 42px 'VT323', monospace";
      const width = ctx.measureText(label).width;
      const height = 36;
      logo.x += logo.dx;
      logo.y += logo.dy;
      if (logo.x <= 0 || logo.x + width >= canvas.width) {
        logo.dx *= -1;
        colorIndex = (colorIndex + 1) % colors.length;
      }
      if (logo.y <= height || logo.y >= canvas.height) {
        logo.dy *= -1;
        colorIndex = (colorIndex + 1) % colors.length;
      }
      ctx.fillStyle = colors[colorIndex];
      ctx.fillText(label, logo.x, logo.y);

      frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", resize);
    };
  }, [active]);

  return (
    <AnimatePresence>
      {active && (
        <motion.canvas
          ref={canvasRef}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.2 } }}
          transition={{ duration: 1.2 }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 150000,
            cursor: "none",
          }}
        />
      )}
    </AnimatePresence>
  );
};

export default Screensaver;
